import { InvariantViolation } from './errors';
import type { ConnectorId, EvseId } from './types';

export type EVSEKind = 'AC' | 'DC';

export const DC_THRESHOLD_W = 50_000;

export interface EVSESnapshot {
  readonly id: EvseId;
  readonly name: string;
  readonly maxPowerW: number;
  readonly connectorIds: readonly ConnectorId[];
  /**
   * FEAT-011 — explicit AC/DC selection. Optional for backward compatibility:
   * topology rows persisted before this field existed have no `kind`, so the
   * `kind` getter falls back to the legacy power-threshold heuristic. New
   * topologies (and migrated old ones) always carry an explicit value.
   */
  readonly kind?: EVSEKind;
}

export class EVSE {
  readonly id: EvseId;
  readonly name: string;
  readonly maxPowerW: number;
  readonly connectorIds: readonly ConnectorId[];
  readonly #kind: EVSEKind | undefined;

  constructor(snapshot: EVSESnapshot) {
    if (snapshot.connectorIds.length < 1) {
      throw new InvariantViolation('EVSE', 'connectorIds.length >= 1', `evseId=${snapshot.id}`);
    }
    if (!(snapshot.maxPowerW > 0)) {
      throw new InvariantViolation('EVSE', 'maxPowerW > 0', `evseId=${snapshot.id}`);
    }
    if (snapshot.name.length === 0) {
      throw new InvariantViolation('EVSE', 'name is non-empty', `evseId=${snapshot.id}`);
    }
    const uniqueConnectors = new Set(snapshot.connectorIds);
    if (uniqueConnectors.size !== snapshot.connectorIds.length) {
      throw new InvariantViolation(
        'EVSE',
        'connectorIds are unique within EVSE',
        `evseId=${snapshot.id}`,
      );
    }
    this.id = snapshot.id;
    this.name = snapshot.name;
    this.maxPowerW = snapshot.maxPowerW;
    this.connectorIds = [...snapshot.connectorIds];
    this.#kind = snapshot.kind;
  }

  get kind(): EVSEKind {
    // Explicit selection wins; fall back to the legacy power-threshold
    // heuristic only for topologies persisted before `kind` existed.
    return this.#kind ?? (this.maxPowerW >= DC_THRESHOLD_W ? 'DC' : 'AC');
  }

  toSnapshot(): EVSESnapshot {
    return {
      id: this.id,
      name: this.name,
      maxPowerW: this.maxPowerW,
      connectorIds: this.connectorIds,
      ...(this.#kind !== undefined ? { kind: this.#kind } : {}),
    };
  }
}
